import { CharStreams, CommonTokenStream } from 'antlr4';
import AioExpressionLexer from '../anltr/AioExpressionLexer.js';
import AioExpressionParser from '../anltr/AioExpressionParser.js';
import AioExpressionEvalVisitor from './AioExpressionEvalVisitor.js';
import AioExpressionParsingException from '../exception/AioExpressionParsingException.js';
import CustomErrorListener from '../listeners/ErrorListener.js';
import { EventMarketOutcomeNamesDTO } from '../shared/dtos';
/**
 * A parser for the AIO expression language that can evaluate expressions
 * and calculate boolean or string results.
 */
export default class AioExpressionLanguageParser {
    /**
     * Creates an input context by lexing and parsing the provided expression.
     * This method tokenizes the expression and then parses it to generate an
     * InputContext, used for further processing.
     *
     * @param {string} expression - The expression string to be lexed and parsed.
     * @returns {InputContext} - The resulting InputContext from the parsed expression.
     */
    createInputContext(expression) {
        const lexer = new AioExpressionLexer(CharStreams.fromString(expression));
        lexer.removeErrorListeners();
        lexer.addErrorListener(new CustomErrorListener());
        const parser = new AioExpressionParser(new CommonTokenStream(lexer));
        return parser.input();
    }
    /**
     * Processes the provided variables and populates a Map with parsed key-value pairs.
     * If the variable is an instance of `EventMarketOutcomeNamesDTO`, it is collected separately
     * under a specific key. If the variable is a string, it is split and processed as a key-value pair.
     *
     * @param {EventMarketOutcomeNamesDTO[] | string[]} variables - The list of variables to process, either
     * an array of `EventMarketOutcomeNamesDTO` objects or strings in the format "key=value".
     * @returns {Map<string, Object>} - A map containing the processed variables.
     * @throws {AioExpressionParsingException} - Throws an exception if an unsupported variable type is encountered.
     */
    processVariables(variables) {
        const vars = new Map();
        const eventMarketOutcomeNames = [];
        for (const variable of variables)
            for (const v of Array.isArray(variable) ? variable : [variable]) {
                if (v instanceof EventMarketOutcomeNamesDTO)
                    eventMarketOutcomeNames.push(v);
                else if (typeof v === 'string') {
                    const [name, value] = v.split('=');
                    vars.set(name, value.startsWith('[') ? value.slice(1, -1).split(',') : value);
                }
                else
                    throw new AioExpressionParsingException(`Unsupported variable type: ${typeof v}`);
            }
        if (eventMarketOutcomeNames.length)
            vars.set(AioExpressionEvalVisitor.CONCAT_EMO_COMPETITOR_NAMES, eventMarketOutcomeNames);
        return vars;
    }
    /**
     * Parses and evaluates an AIO expression.
     *
     * @param input - The input string to parse and evaluate.
     * @param variables - An optional array of variable definitions (default is an empty array).
     * @param wrapCommand - Specifies whether to wrap the input in double curly braces (default is false).
     * @returns The evaluated result as an object.
     * @throws {AioExpressionParsingException} If there is an error during parsing or evaluation.
     */
    parseInput(input, variables = [], wrapCommand = false) {
        const expression = wrapCommand ? `{{${input}}}` : input;
        try {
            const tree = this.createInputContext(expression);
            const evalVisitor = new AioExpressionEvalVisitor();
            const vars = this.processVariables(variables);
            return evalVisitor.setVariables(vars).visit(tree);
        }
        catch (e) {
            throw e;
        }
    }
    /**
     * Calculates a boolean result from the input string.
     *
     * @param input - The input string to parse and calculate.
     * @returns The calculated result as a boolean.
     * @throws {AioExpressionParsingException} If the input string doesn't result in a boolean.
     */
    calculateBoolean(input, variables) {
        const result = this.parseInput(input, variables, true).toString();
        if (result === 'true')
            return true;
        else if (result === 'false')
            return false;
        else
            throw new AioExpressionParsingException("Method didn't result with boolean");
    }
    /**
     * Parses and calculates the result of an result string with optional variables.
     *
     * @param input - The input string to parse and calculate.
     * @param variables - An optional array of variable names (default is undefined).
     * @returns The calculated result as a string.
     */
    calculateString(input, variables) {
        return this.parseInput(input, variables, false).toString();
    }
}
